import { getApiError } from '@/configs/axios.config'
import { apiErrorCode, apiErrorDetails } from '@/lib/api-error-body'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import {
  OGSM_NODE_HAS_DATA,
  isOgsmRemovalBlocker,
  isOgsmRemovalImpact,
} from '@/features/ogsm/removal'
import type { OgsmRemovalImpact } from '@/types/api'

const GENERIC = 'Something went wrong. Try again.'

/** The backend's reason for a locked or read-only session, or null. */
export function ogsmConflictMessage(error: unknown): string | null {
  const { statusCode, message } = getApiError(error)
  return statusCode === 409 ? message : null
}

/** Inline text: the backend message for 4xx, a fixed one for anything else. */
export function ogsmErrorMessage(error: unknown): string {
  const { statusCode, message } = getApiError(error)
  return statusCode !== undefined && statusCode >= 400 && statusCode < 500
    ? message
    : GENERIC
}

/** True when the backend refused a publish on its Scorecard rules (400): the dialog adds what to do next. */
export function ogsmPublishRefused(error: unknown): boolean {
  return getApiError(error).statusCode === 400
}

/** 409 OGSM_NODE_HAS_DATA, whatever its details. */
export function ogsmDeleteDataAppeared(error: unknown): boolean {
  return (
    getApiError(error).statusCode === 409 &&
    apiErrorCode(error) === OGSM_NODE_HAS_DATA
  )
}

/** 403, 404, and any 409 except OGSM_NODE_HAS_DATA: a retry cannot fix them. 5xx/network: false. */
export function ogsmDeleteRefused(error: unknown): boolean {
  const { statusCode } = getApiError(error)
  if (statusCode === 403 || statusCode === 404) return true
  return statusCode === 409 && !ogsmDeleteDataAppeared(error)
}

/** The impact from a 409 OGSM_NODE_HAS_DATA when details is a valid impact with outcome ARCHIVE; else null. */
export function ogsmDeleteHasData(error: unknown): OgsmRemovalImpact | null {
  if (!ogsmDeleteDataAppeared(error)) return null
  const details = apiErrorDetails(error)
  return isOgsmRemovalImpact(details) && details.outcome === 'ARCHIVE'
    ? details
    : null
}

/** Inline line: 404 → OGSM_COPY.nodeNotFound (the backend's names an internal id); 409 with a blocker code → OGSM_COPY.deleteBlocked; else ogsmErrorMessage. */
export function ogsmDeleteErrorMessage(error: unknown, kind: string): string {
  const { statusCode } = getApiError(error)
  if (statusCode === 404) return OGSM_COPY.nodeNotFound
  const code = apiErrorCode(error)
  if (statusCode === 409 && isOgsmRemovalBlocker(code)) {
    return OGSM_COPY.deleteBlocked(code, kind)
  }
  return ogsmErrorMessage(error)
}
